import React, { useState, useEffect } from 'react';
import {
  CheckCircle, Clock, Lock, ChevronDown, ChevronUp,
  FileText, HelpCircle, ClipboardCheck, BookOpen, Target,
  TrendingUp, Award
} from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '../api';
import { getTopicImage, getTopicCategory } from '../utils/topicImages';
import QuizPlayer from './QuizPlayer';

const LearningPath = () => {
  const [paths, setPaths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedTopic, setExpandedTopic] = useState(null);
  const [notesDialog, setNotesDialog] = useState(false);
  const [quizDialog, setQuizDialog] = useState(false);
  const [assessmentDialog, setAssessmentDialog] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [selectedPath, setSelectedPath] = useState(null);
  const [quizStatus, setQuizStatus] = useState({});

  useEffect(() => {
    fetchLearningPath();
    fetchQuizStatus();
  }, []);

  const fetchLearningPath = async () => {
    try {
      const res = await api.get('/student/learning-path');
      setPaths(res.data || []);
    } catch {
      toast.error('Failed to load learning path');
    }
    setLoading(false);
  };

  const fetchQuizStatus = async () => {
    try {
      const res = await api.get('/student/quiz-status');
      setQuizStatus(res.data || {});
    } catch { /* non-critical */ }
  };

  const totalTopics = paths.reduce((s, p) => s + p.total_topics, 0);
  const totalCompleted = paths.reduce((s, p) => s + p.completed_topics, 0);
  const overallPct = totalTopics > 0 ? Math.round((totalCompleted / totalTopics) * 100) : 0;

  const toggleTopic = (pathId, topicIndex) => {
    const key = `${pathId}_${topicIndex}`;
    setExpandedTopic(expandedTopic === key ? null : key);
  };

  const openDetail = (type, topic, path) => {
    setSelectedTopic(topic);
    setSelectedPath(path);
    if (type === 'notes') setNotesDialog(true);
    if (type === 'quiz') setQuizDialog(true);
    if (type === 'assessment') setAssessmentDialog(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="learning-path-section">
      <div>
        <h1 className="text-3xl font-bold text-slate-900" data-testid="learning-path-title">Learning Path</h1>
        <p className="text-sm text-slate-500 mt-1">Track your progress across all courses</p>
      </div>

      {/* Overall Progress Bar */}
      <div className="glass-card rounded-2xl p-5 shadow-sm" data-testid="overall-progress">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-blue-600" />
            <span className="font-bold text-slate-900">Overall Progress</span>
          </div>
          <span className="text-2xl font-extrabold text-blue-600">{overallPct}%</span>
        </div>
        <Progress value={overallPct} className="h-3" />
      </div>

      {/* Course Paths */}
      {paths.length === 0 ? (
        <div className="glass-card rounded-2xl p-10 text-center">
          <Target className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 font-medium text-base">No curriculum assigned yet</p>
          <p className="text-slate-400 text-sm mt-1">Your teacher will assign a learning path for you</p>
        </div>
      ) : (
        paths.map((path) => (
          <div key={path.id} className="glass-card rounded-2xl overflow-hidden shadow-sm" data-testid={`path-${path.id}`}>
            {/* Course Header */}
            <div className="p-5 border-b border-slate-100">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-bold text-slate-900">{path.course_name}</h2>
                  <p className="text-sm text-slate-500 mt-0.5">
                    {path.vertical}{path.category ? ` / ${path.category}` : ''}{path.teacher_name ? ` — ${path.teacher_name}` : ''}
                  </p>
                </div>
                <div className="text-right">
                  <span className="text-2xl font-extrabold text-blue-600">{path.percentage}%</span>
                </div>
              </div>
              <Progress value={path.percentage} className="h-2 mt-3" />
            </div>

            {/* Topics List */}
            <div className="divide-y divide-slate-50">
              {path.topics.map((topic, idx) => {
                const isExpanded = expandedTopic === `${path.id}_${idx}`;
                const isCompleted = topic.status === 'completed';
                const isInProgress = topic.status === 'in_progress';
                const isLocked = topic.status === 'locked';
                const isClickable = !isLocked;
                const qs = quizStatus[topic.title];

                return (
                  <div key={idx} data-testid={`topic-card-${path.id}-${idx}`}>
                    {/* Topic Row */}
                    <button
                      onClick={() => isClickable && toggleTopic(path.id, idx)}
                      disabled={isLocked}
                      className={`w-full flex items-center gap-4 p-4 text-left transition-all ${
                        isLocked ? 'opacity-50 cursor-not-allowed' : 'hover:bg-slate-50 cursor-pointer'
                      } ${isExpanded ? 'bg-slate-50' : ''}`}
                      data-testid={`topic-btn-${path.id}-${idx}`}
                    >
                      {/* Topic Image */}
                      <div className={`w-14 h-14 rounded-xl flex-shrink-0 relative overflow-hidden group ${isLocked ? 'grayscale' : ''}`}>
                        <img
                          src={getTopicImage(topic.title, path.course_name, false)}
                          alt={topic.title}
                          loading="lazy"
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                        <div className="absolute bottom-0.5 left-0.5 px-1 py-0.5 bg-white/90 rounded text-[8px] text-slate-700 font-bold">
                          {getTopicCategory(topic.title, path.course_name)}
                        </div>
                      </div>

                      {/* Topic Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-slate-400 font-semibold">Topic {topic.class_number || idx + 1}</span>
                          {topic.date && <span className="text-xs text-slate-400">{topic.date}</span>}
                          {qs?.has_quiz && qs.attempts > 0 && (
                            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${qs.best_pct >= 70 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                              <Award className="w-3 h-3" /> {qs.best_pct}%
                            </span>
                          )}
                          {qs?.has_quiz && qs.attempts === 0 && isCompleted && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-violet-100 text-violet-700">
                              Quiz Ready
                            </span>
                          )}
                        </div>
                        <p className={`font-bold truncate ${isCompleted ? 'text-emerald-700' : isInProgress ? 'text-blue-700' : isLocked ? 'text-slate-400' : 'text-slate-700'}`}>
                          {topic.title}
                        </p>
                      </div>

                      {/* Status Badge */}
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {isCompleted && (
                          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-700" data-testid={`status-completed-${idx}`}>
                            <CheckCircle className="w-4 h-4" />
                            <span className="text-xs font-bold">Completed</span>
                          </div>
                        )}
                        {isInProgress && (
                          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-100 text-blue-700" data-testid={`status-progress-${idx}`}>
                            <Clock className="w-4 h-4" />
                            <span className="text-xs font-bold">In Progress</span>
                          </div>
                        )}
                        {isLocked && (
                          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-100 text-slate-400" data-testid={`status-locked-${idx}`}>
                            <Lock className="w-4 h-4" />
                            <span className="text-xs font-bold">Locked</span>
                          </div>
                        )}
                        {topic.status === 'carried_forward' && (
                          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-orange-100 text-orange-700">
                            <Clock className="w-4 h-4" />
                            <span className="text-xs font-bold">Carried</span>
                          </div>
                        )}
                        {isClickable && (
                          isExpanded
                            ? <ChevronUp className="w-4 h-4 text-slate-400" />
                            : <ChevronDown className="w-4 h-4 text-slate-400" />
                        )}
                      </div>
                    </button>

                    {/* Expanded Detail Panel */}
                    {isExpanded && (
                      <div className="px-4 pb-4 pt-1 bg-slate-50" data-testid={`topic-expanded-${path.id}-${idx}`}>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 ml-[4.5rem]">
                          <button
                            onClick={() => openDetail('notes', topic, path)}
                            className="flex items-center gap-3 p-4 bg-white rounded-xl border border-slate-200 hover:border-blue-300 hover:shadow-md transition-all group"
                            data-testid={`notes-btn-${path.id}-${idx}`}
                          >
                            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                              <FileText className="w-5 h-5 text-blue-600" />
                            </div>
                            <div className="text-left">
                              <p className="font-bold text-slate-900 text-sm">Class Notes</p>
                              <p className="text-xs text-slate-400">View notes & feedback</p>
                            </div>
                          </button>
                          <button
                            onClick={() => openDetail('quiz', topic, path)}
                            className="flex items-center gap-3 p-4 bg-white rounded-xl border border-slate-200 hover:border-violet-300 hover:shadow-md transition-all group relative"
                            data-testid={`quiz-btn-${path.id}-${idx}`}
                          >
                            <div className="w-10 h-10 rounded-lg bg-violet-100 flex items-center justify-center group-hover:bg-violet-200 transition-colors">
                              <HelpCircle className="w-5 h-5 text-violet-600" />
                            </div>
                            <div className="text-left">
                              <p className="font-bold text-slate-900 text-sm">Quiz</p>
                              <p className="text-xs text-slate-400">
                                {isCompleted ? (qs?.attempts > 0 ? `Best: ${qs.best_pct}%` : 'Take quiz') : 'Complete topic first'}
                              </p>
                            </div>
                            {qs?.has_quiz && qs.attempts > 0 && (
                              <div className={`absolute top-2 right-2 w-5 h-5 rounded-full flex items-center justify-center ${qs.best_pct >= 70 ? 'bg-emerald-500' : 'bg-amber-500'}`}>
                                <CheckCircle className="w-3 h-3 text-white" />
                              </div>
                            )}
                            {!isCompleted && (
                              <Lock className="absolute top-2 right-2 w-4 h-4 text-slate-300" />
                            )}
                          </button>
                          <button
                            onClick={() => openDetail('assessment', topic, path)}
                            className="flex items-center gap-3 p-4 bg-white rounded-xl border border-slate-200 hover:border-emerald-300 hover:shadow-md transition-all group"
                            data-testid={`assessment-btn-${path.id}-${idx}`}
                          >
                            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center group-hover:bg-emerald-200 transition-colors">
                              <ClipboardCheck className="w-5 h-5 text-emerald-600" />
                            </div>
                            <div className="text-left">
                              <p className="font-bold text-slate-900 text-sm">Assessment</p>
                              <p className="text-xs text-slate-400">Evaluate progress</p>
                            </div>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))
      )}

      {/* Class Notes Dialog */}
      <Dialog open={notesDialog} onOpenChange={setNotesDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Class Notes — {selectedTopic?.title}</DialogTitle>
            <DialogDescription className="text-slate-500 text-sm">View class notes and teacher feedback</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2" data-testid="notes-dialog-content">
            {selectedTopic?.feedback ? (
              <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                <p className="text-sm font-semibold text-blue-900 mb-1">Teacher Feedback</p>
                <p className="text-sm text-slate-700">{selectedTopic.feedback}</p>
              </div>
            ) : (
              <div className="text-center py-8">
                <FileText className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                <p className="text-slate-500 font-medium">No notes available yet</p>
                <p className="text-sm text-slate-400 mt-1">Notes will appear after your teacher adds them</p>
              </div>
            )}
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
              <p className="text-xs text-slate-500">Course: <span className="font-bold text-slate-700">{selectedPath?.course_name}</span></p>
              <p className="text-xs text-slate-500">Status: <span className="font-bold capitalize text-slate-700">{selectedTopic?.status?.replace('_', ' ')}</span></p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Quiz Dialog */}
      <Dialog open={quizDialog} onOpenChange={(open) => { setQuizDialog(open); if (!open) fetchQuizStatus(); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Quiz — {selectedTopic?.title}</DialogTitle>
            <DialogDescription className="text-slate-500 text-sm">Test your understanding of this topic</DialogDescription>
          </DialogHeader>
          <div data-testid="quiz-dialog-content">
            {selectedTopic && (
              <QuizPlayer
                topicTitle={selectedTopic.title}
                topicStatus={selectedTopic.status}
                onClose={() => { setQuizDialog(false); fetchQuizStatus(); }}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Assessment Dialog */}
      <Dialog open={assessmentDialog} onOpenChange={setAssessmentDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Assessment — {selectedTopic?.title}</DialogTitle>
            <DialogDescription className="text-slate-500 text-sm">Evaluate your progress on this topic</DialogDescription>
          </DialogHeader>
          <div className="text-center py-8" data-testid="assessment-dialog-content">
            <ClipboardCheck className="w-12 h-12 text-emerald-300 mx-auto mb-3" />
            <p className="text-slate-500 font-medium">Assessment coming soon</p>
            <p className="text-sm text-slate-400 mt-1">Assessments will be available after completing this topic</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LearningPath;
